import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import { isVoidFloorAssemblyOverride } from "doc/elements/floor/floorAssembly";
import type { PlanGenerationContext } from "doc/plans/planGeometrySource";
import { collectPolylinesFromExtrusions, floorExtrusionPolylinesNew } from "lib/house/floorExtrusionPolylines";

/**
 * Generates plan-view polylines for subfloor extrusions on framed and slab floor assemblies.
 */
export function makeSheetGeometryFromFloorSubFlooring({ building, levelId }: PlanGenerationContext): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const { framedFloorAssemblies, slabFloorAssemblies, levels, horizontalAssemblyFloors } = building;
  const handle = levels[levelId].handle;

  for (const boundaryId in framedFloorAssemblies) {
    const assembly = framedFloorAssemblies[boundaryId];
    if (assembly.levelId !== levelId) {
      continue;
    }
    // Assemblies are keyed by bay/boundary id; the floor entity is under parentFloorId.
    const floorEntity = horizontalAssemblyFloors[assembly.parentFloorId] ?? horizontalAssemblyFloors[boundaryId];
    if (!floorEntity || floorEntity.disabled || isVoidFloorAssemblyOverride(floorEntity)) {
      continue;
    }

    const polylines = floorExtrusionPolylinesNew();
    collectPolylinesFromExtrusions(assembly.subfloor, polylines);
    if (polylines.outlines.length > 0) {
      result.push(sheetPolylineGeometryNew({ handle, paths: polylines.outlines }));
    }
    if (polylines.openings.length > 0) {
      result.push(sheetPolylineGeometryNew({ handle, paths: polylines.openings }));
    }
  }

  for (const boundaryId in slabFloorAssemblies) {
    const assembly = slabFloorAssemblies[boundaryId];
    if (assembly.levelId !== levelId) {
      continue;
    }
    // Assemblies are keyed by bay/boundary id; the floor entity is under parentFloorId.
    const floorEntity = horizontalAssemblyFloors[assembly.parentFloorId] ?? horizontalAssemblyFloors[boundaryId];
    if (!floorEntity || floorEntity.disabled || isVoidFloorAssemblyOverride(floorEntity)) {
      continue;
    }

    const polylines = floorExtrusionPolylinesNew();
    collectPolylinesFromExtrusions(assembly.subfloor, polylines);
    if (polylines.outlines.length > 0) {
      result.push(sheetPolylineGeometryNew({ handle, paths: polylines.outlines }));
    }
    if (polylines.openings.length > 0) {
      result.push(sheetPolylineGeometryNew({ handle, paths: polylines.openings }));
    }
  }

  return result;
}
